import { IconButton } from './primitives';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

/**
 * The only element in the product with a shadow, because it floats over
 * everything else. Undo lives here: a placement on Today is one append and
 * its undo is another, so "put it back" is exact.
 */
export interface Toast {
  id: string;
  text: string;
  tone?: 'danger' | undefined;
  undo?: (() => void) | undefined;
  /** A place to go, e.g. "Take a look" (gallery G:518-527). */
  link?: { label: string; to: string } | undefined;
}

type Push = (toast: Omit<Toast, 'id'>) => void;

const Ctx = createContext<Push>(() => {});
export const useToast = (): Push => useContext(Ctx);

export function ToastHost({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback(
    (id: string) => setToasts((all) => all.filter((x) => x.id !== id)),
    [],
  );
  const push = useCallback<Push>(
    (t) => {
      const id = Math.random().toString(36).slice(2);
      // Three at most (gallery rule 04): a fourth pushes out the oldest,
      // which has been on screen longest and read most.
      setToasts((all) => [...all, { ...t, id }].slice(-3));
      setTimeout(() => dismiss(id), 6000);
    },
    [dismiss],
  );
  const value = useMemo(() => push, [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-7 left-1/2 z-[100] flex w-[calc(100%-32px)] max-w-[560px] -translate-x-1/2 flex-col items-center gap-2.5 phone:bottom-[calc(72px+env(safe-area-inset-bottom))]"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto flex w-full animate-[nrise_220ms_ease-brand_both] items-center gap-4 border px-[18px] py-3.5 text-[var(--toast-fg)] shadow-toast ${
              t.tone === 'danger' ? 'border-danger bg-danger !text-[var(--danger-fg)]' : 'border-[var(--toast-edge)] bg-[var(--toast-bg)]'
            }`}
          >
            <span className="text-dense">{t.text}</span>
            {t.link && (
              <button
                type="button"
                onClick={() => {
                  dismiss(t.id);
                  void navigate(t.link!.to);
                }}
                className="ml-auto min-h-tap whitespace-nowrap text-label uppercase tracking-button underline underline-offset-[3px]"
              >
                {t.link.label}
              </button>
            )}
            {t.undo && (
              <button
                type="button"
                onClick={() => {
                  dismiss(t.id);
                  t.undo?.();
                }}
                className="ml-auto min-h-tap whitespace-nowrap text-label uppercase tracking-button underline underline-offset-[3px]"
              >
                Undo
              </button>
            )}
            <IconButton
              label="Dismiss"
              glyph="close"
              ghost
              onClick={() => dismiss(t.id)}
              className={`!text-current opacity-70 hover:opacity-100 ${t.undo || t.link ? '' : 'ml-auto'}`}
            />
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
